import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";

const STEPS = [
  "Legal bits",
  "Where they work",
  "Who to talk to",
  "Account setup",
  "Sign off",
];

export default function NewCustomer() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [f, setF] = useState({
    legal_name: "",
    trading_name: "",
    abn: "",
    site_address: "",
    contact_name: "",
    contact_role: "",
    contact_phone: "",
    contact_email: "",
    accounts_name: "",
    accounts_email: "",
    account_status: "setup-in-progress",
  });

  const set = (k, v) => {
    setF({ ...f, [k]: v });
    if (fieldErrors[k] || fieldErrors[`${k}_group`]) {
      const { [k]: _a, [`${k}_group`]: _b, ...rest } = fieldErrors;
      setFieldErrors(rest);
    }
  };

  const validateStep = () => {
    const errs = {};
    if (step === 1) {
      if (!f.legal_name.trim()) errs.legal_name = "The legal name is needed before you can continue.";
    }
    if (step === 3) {
      if (!f.contact_name.trim()) errs.contact_name = "A site contact name is needed before you can continue.";
      if (!f.contact_phone.trim() && !f.contact_email.trim()) {
        errs.contact_phone = "Add a phone number or an email — at least one so we can reach them.";
        errs.contact_email = "Add a phone number or an email — at least one so we can reach them.";
      }
    }
    return errs;
  };

  const goNext = () => {
    const errs = validateStep();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setStep(step + 1);
  };

  const buildPayload = (asProspect) => ({
    legal_name: f.legal_name,
    trading_name: f.trading_name || null,
    abn: f.abn || null,
    site_address: f.site_address || null,
    account_status: asProspect ? "setup-in-progress" : f.account_status,
    is_prospect: asProspect,
    contacts: [
      ...(f.contact_name ? [{ name: f.contact_name, role: f.contact_role || "Site", phone: f.contact_phone || null, email: f.contact_email || null }] : []),
      ...(f.accounts_name ? [{ name: f.accounts_name, role: "Accounts", phone: null, email: f.accounts_email || null }] : []),
    ],
  });

  const submit = async (asProspect = false) => {
    if (!f.legal_name.trim()) {
      setErr("Legal name is required.");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/customers", buildPayload(asProspect));
      nav("/customers");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/drafts", {
        kind: "customer",
        title: (f.legal_name || "New customer draft"),
        payload: f,
      });
      nav("/customers");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const canSaveProspect = f.legal_name.trim().length > 0;

  return (
    <div data-testid="new-customer-page">
      <PageHeader title="New customer setup" subtitle="Five steps — or save as a prospect right after step 1" />

      <div className="ach-card" style={{marginBottom: 12}}>
        <h2 className="ach-card__title">Five steps — nothing to remember</h2>
        <div className="ach-steps">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const cls = n < step ? "done" : (n === step ? "current" : "");
            return <div key={s} className={`ach-step ${cls}`} data-testid={`nc-step-${n}`}>{n}. {s}</div>;
          })}
        </div>
      </div>

      <div className="ach-card">
        <h2 className="ach-card__title">Step {step} — {STEPS[step-1]}</h2>

        {step === 1 && (
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Legal name (as on invoices) <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input data-testid="nc-legal-name" value={f.legal_name} onChange={(e)=>set("legal_name", e.target.value)} placeholder="e.g. ACME Civil Pty Ltd"
                style={fieldErrors.legal_name ? {borderColor:"var(--ach-red)"} : undefined} />
              {fieldErrors.legal_name && <div className="ach-field-error" data-testid="nc-error-legal-name" style={{color:"var(--ach-red)", fontSize:12, marginTop:4}}>{fieldErrors.legal_name}</div>}
            </div>
            <div className="ach-field">
              <label>Trading name (if different)</label>
              <input data-testid="nc-trading-name" value={f.trading_name} onChange={(e)=>set("trading_name", e.target.value)} />
            </div>
            <div className="ach-field">
              <label>ABN <span style={{color:"var(--ach-text-2)", fontWeight:400}}>(required before you can start a hire)</span></label>
              <input data-testid="nc-abn" value={f.abn} onChange={(e)=>set("abn", e.target.value)} placeholder="11 111 111 111" inputMode="numeric" />
            </div>
            <div className="ach-explainer" style={{background:"var(--ach-page-bg)"}}>
              <strong>No ABN yet?</strong> That's fine — save them as a <em>prospect</em> below and you'll still be able to quote them.
              You'll need the ABN and full details before you can actually start a hire.
            </div>
            {canSaveProspect && (
              <div className="ach-explainer" style={{gridColumn:"1 / -1"}}>
                <button className="ach-btn ach-btn--ghost" onClick={()=>submit(true)} disabled={busy} data-testid="nc-save-prospect">
                  {busy ? "Saving…" : "Save as prospect (quote them now)"}
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Where do they work? (main address)</label>
              <textarea rows={3} data-testid="nc-site-address" value={f.site_address} onChange={(e)=>set("site_address", e.target.value)} placeholder="e.g. Yarra Depot, 145 Punt Rd, Richmond VIC 3121" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Site contact — name <span style={{color:"var(--ach-red)"}}>*</span></label>
              <input data-testid="nc-contact-name" value={f.contact_name} onChange={(e)=>set("contact_name", e.target.value)}
                style={fieldErrors.contact_name ? {borderColor:"var(--ach-red)"} : undefined} />
              {fieldErrors.contact_name && <div className="ach-field-error" data-testid="nc-error-contact-name" style={{color:"var(--ach-red)", fontSize:12, marginTop:4}}>{fieldErrors.contact_name}</div>}
            </div>
            <div className="ach-field"><label>What they do</label><input data-testid="nc-contact-role" value={f.contact_role} onChange={(e)=>set("contact_role", e.target.value)} placeholder="Site manager" /></div>
            <div className="ach-field">
              <label>Phone <span style={{color:"var(--ach-text-2)", fontWeight:400}}>(phone or email)</span></label>
              <input data-testid="nc-contact-phone" type="tel" inputMode="tel" autoComplete="tel" value={f.contact_phone} onChange={(e)=>set("contact_phone", e.target.value)} placeholder="e.g. 0400 123 456"
                style={fieldErrors.contact_phone ? {borderColor:"var(--ach-red)"} : undefined} />
              {fieldErrors.contact_phone && <div className="ach-field-error" data-testid="nc-error-contact-phone" style={{color:"var(--ach-red)", fontSize:12, marginTop:4}}>{fieldErrors.contact_phone}</div>}
            </div>
            <div className="ach-field">
              <label>Email <span style={{color:"var(--ach-text-2)", fontWeight:400}}>(phone or email)</span></label>
              <input data-testid="nc-contact-email" type="email" value={f.contact_email} onChange={(e)=>set("contact_email", e.target.value)}
                style={fieldErrors.contact_email ? {borderColor:"var(--ach-red)"} : undefined} />
              {fieldErrors.contact_email && <div className="ach-field-error" data-testid="nc-error-contact-email" style={{color:"var(--ach-red)", fontSize:12, marginTop:4}}>{fieldErrors.contact_email}</div>}
            </div>
            <div className="ach-field"><label>Accounts contact — name (optional)</label><input data-testid="nc-accounts-name" value={f.accounts_name} onChange={(e)=>set("accounts_name", e.target.value)} /></div>
            <div className="ach-field"><label>Accounts email (optional)</label><input data-testid="nc-accounts-email" type="email" value={f.accounts_email} onChange={(e)=>set("accounts_email", e.target.value)} /></div>
          </div>
        )}

        {step === 4 && (
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Account status right now</label>
              <select data-testid="nc-account-status" value={f.account_status} onChange={(e)=>set("account_status", e.target.value)}>
                <option value="setup-in-progress">Setup in progress — no hires until approved</option>
                <option value="approved">Approved — good to hire to</option>
                <option value="needs-recheck">Worth a re-check — flag for a call</option>
              </select>
              <div className="helper">In Phase 2 this triggers an automatic credit check through Accessii. For now it&apos;s just recorded.</div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <div className="ach-explainer" style={{marginBottom: 12}}>
              <strong>When you press add</strong>: this customer becomes available to hire to (subject to account status). Nothing is emailed yet — Emails records what would have been sent.
              {err && <div style={{color:"var(--ach-red)", marginTop:8}} data-testid="nc-submit-error">{err}</div>}
            </div>
            <table className="ach-table" style={{marginBottom:16}}>
              <tbody>
                <tr><td style={{color:"var(--ach-text-2)", width: 180}}>Legal name</td><td><strong>{f.legal_name}</strong></td></tr>
                <tr><td style={{color:"var(--ach-text-2)"}}>ABN</td><td>{f.abn || <em style={{color:"var(--ach-text-2)"}}>not provided — will be a prospect</em>}</td></tr>
                <tr><td style={{color:"var(--ach-text-2)"}}>Site contact</td><td>{f.contact_name} · {f.contact_phone || f.contact_email}</td></tr>
                <tr><td style={{color:"var(--ach-text-2)"}}>Account</td><td>{f.account_status.replace(/-/g, " ")}</td></tr>
              </tbody>
            </table>
            <div style={{textAlign:"center"}}>
              <button className="ach-btn ach-btn--primary" onClick={()=>submit(!f.abn.trim())} disabled={busy} data-testid="nc-submit" style={{padding:"14px 40px", fontSize: 14}}>
                {busy ? "Adding…" : (f.abn.trim() ? "Add this customer" : "Save as prospect")}
              </button>
            </div>
          </div>
        )}

        <div style={{display:"flex", justifyContent:"space-between", marginTop: 20}}>
          <button className="ach-btn ach-btn--grey" onClick={()=>step > 1 ? setStep(step - 1) : nav("/customers")} data-testid="nc-back">
            {step > 1 ? "← Back" : "Cancel"}
          </button>
          <div style={{display:"flex", gap:8}}>
            <button className="ach-btn ach-btn--grey" onClick={saveDraft} disabled={busy || !f.legal_name} data-testid="nc-save-later">
              {busy ? "…" : "Save draft for later"}
            </button>
            {step < 5 && (
              <button className="ach-btn ach-btn--primary" onClick={goNext} data-testid="nc-next">Next →</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
